/**
 * **FI-35 (D-FI35)** — Service SSOT per l'acquisizione di una mossa al PG.
 *
 * Una "acquisizione mossa" è l'evento «il PG acquisisce questa specifica
 * mossa con queste scelte e queste spell selezionate». L'evento può
 * accadere in 4 contesti (vedi `AcquisitionSource`) ma il flow di
 * applicazione degli effetti collaterali è uno solo — questo service.
 *
 * **Why questo service esiste** (FI-35):
 * Prima del refactor, la logica di "applica gli effetti di una mossa al
 * Character" era distribuita tra:
 *  - `finalizeCharacter` (builder L1) — pipeline ad-hoc per spell L1 +
 *    follower derivati
 *  - `applyLevelUp` (level-up) — gestiva replaces + spellsAdded delegati
 *    al caller
 *  - `SectionSpellSelection` — pipeline UI dedicata Mago/Warlock L1
 *    completamente disconnessa da `MoveSchema.grantsSpells`
 * Risultato: quando una mossa veniva acquisita post-creazione (es. Vincolo
 * del Tomo via grantedChoice in D5), gli effetti collaterali del setup-L1
 * non venivano triggeri. Questo service centralizza la logica.
 *
 * **Cosa fa `applyMoveAcquisition`** (atomicamente):
 *  1. Rimuove le mosse `replaces` da `Character.moves`
 *  2. Aggiunge `acquiredMove.moveRef` a `Character.moves` (con eventuale setup)
 *  3. Applica spell-grants di tipo `'auto'` (all-of-level, fixed-list) →
 *     aggiunge spell auto-resolved a `Character.spells` (dedupe)
 *  4. Propaga `acquiredMove.spellsSelected` (grants di tipo `'pick'`) →
 *     aggiunge a `Character.spells` (dedupe)
 *
 * **Cosa NON fa**:
 *  - Re-derivazione `Character.followers`: spetta all'orchestratore
 *    (`applyLevelUp`, `finalizeCharacter`) chiamare `getFollowersFromDraft`
 *    DOPO aver processato tutte le mosse acquisite in una transazione.
 *  - Validazione `CharacterSchema.parse`: stesso motivo, l'orchestratore
 *    parse il Character finale alla fine.
 *  - Stat-up / HP-recalc / `levelUpHistory`: roba specifica del flow
 *    level-up, gestita da `applyLevelUp`.
 *
 * **Idempotenza**: chiamare `applyMoveAcquisition` 2 volte con lo stesso
 * `acquiredMove` NON è idempotente (aggiungerà la mossa 2 volte). Il
 * controllo di duplicazione è responsabilità dell'orchestratore (in
 * `applyLevelUp` è già garantito da `validateMoveAvailability`).
 */

import { findMoveByIdGlobal } from '../data/moves';
import { findSpellsByClass } from '../data/spells';
import type { Character, MoveRef, SpellRef } from '../schemas/character';
import type { ClassDefinition } from '../schemas/classDefinition';
import type { ClassId } from '../schemas/classId';
import {
  hasMoveSetup,
  isAutoSpellGrantLevelRule,
  type Move,
  type SpellGrant,
} from '../schemas/move';
import type { AcquiredMove } from '../schemas/moveAcquisition';
import { getBundleMovesToApply } from './bundle';
import { hasMoveRefInCharacter } from './moveLookup';

/**
 * Requisiti di acquisizione di una mossa: cosa la UI deve esporre al
 * giocatore (step extra) e cosa il service applica automaticamente.
 *
 * Computato puro da `getMoveAcquisitionRequirements(move)`.
 *
 * Consumato da:
 *  - Hook UI `useMoveAcquisitionSteps` (decide quali step extra mostrare)
 *  - Predicate del builder L1 (`isSectionComplete` per spells/follower)
 *  - Test
 */
export interface MoveAcquisitionRequirements {
  /** La mossa ha setup interattivo (`choices`/`choiceGroups`/`narrativeFields`). */
  needsSetup: boolean;
  /** Grants spell di famiglia "pick": il giocatore sceglie N spell. */
  pickSpellGrants: readonly SpellGrant[];
  /** Grants spell di famiglia "auto": applicate dal service, niente scelta. */
  autoSpellGrants: readonly SpellGrant[];
  /** La mossa istanzia un follower (`followerTemplateId` popolato). */
  needsFollowerSetup: boolean;
  /** La mossa concede sotto-scelta granted (FI-31). */
  needsGrantedSubChoice: boolean;
}

/**
 * Filtra `grantsSpells` per trigger e partiziona in pick/auto.
 *
 * **Default trigger**: `'on-take'` (acquisizione singola al momento della
 * presa). Il trigger `'on-each-level-up'` (regola Mago Corpus Arcano) NON
 * è gestito da `applyMoveAcquisition`: vive nel flow `applyLevelUp`
 * tramite `getActiveSpellGrantsForLevelUp`.
 */
export function getMoveAcquisitionRequirements(
  move: Move,
  trigger: 'on-take' = 'on-take',
): MoveAcquisitionRequirements {
  const allGrants = move.grantsSpells ?? [];
  const filtered = allGrants.filter((g) => (g.trigger ?? 'on-take') === trigger);
  const pickSpellGrants = filtered.filter((g) => !isAutoSpellGrantLevelRule(g.levelRule));
  const autoSpellGrants = filtered.filter((g) => isAutoSpellGrantLevelRule(g.levelRule));
  return {
    needsSetup: hasMoveSetup(move),
    pickSpellGrants,
    autoSpellGrants,
    needsFollowerSetup: move.followerTemplateId !== undefined,
    needsGrantedSubChoice: move.grantsMoveChoice !== undefined,
  };
}

/**
 * Risolve una grant di famiglia "auto" nella lista di `SpellRef` da
 * aggiungere al Character.
 *
 *  - `fixed-list`: ritorna direttamente `spellIds` come `SpellRef[]`.
 *  - `all-of-level`: lookup nel catalogo spell della classe-source con
 *    `Spell.level === levelRule.level`. Per ora il `source.kind` non
 *    `'own-class'` (other-classes/any-class) non ha semantica chiara per
 *    grants auto; fallback su `sourceClassId` (la classe della mossa
 *    sorgente del grant).
 *
 * Per grants "pick" ritorna lista vuota (non è auto).
 */
export function resolveAutoSpellGrant(
  grant: SpellGrant,
  sourceClassId: ClassId,
): readonly SpellRef[] {
  if (grant.levelRule.kind === 'fixed-list') {
    return grant.levelRule.spellIds.map((id) => ({ id }));
  }
  if (grant.levelRule.kind === 'all-of-level') {
    const level = grant.levelRule.level;
    return findSpellsByClass(sourceClassId)
      .filter((s) => s.level === level)
      .map((s) => ({ id: s.id }));
  }
  return [];
}

/**
 * Opzioni di {@link applyMoveAcquisition}. Tutte opzionali, default safe.
 *
 * **D7-bundle (FI-17 follow-up)**: opzioni aggiunte per supportare il
 * flow di acquisizione bundle all-inclusive (manuale V1 rr. 1764-1773).
 */
export interface ApplyMoveAcquisitionOptions {
  /**
   * Setup compilato per le mosse del bundle che richiedono setup
   * interattivo (`hasMoveSetup === true`). Chiavi: `moveId` della mossa
   * del bundle; valori: campi di {@link MoveRef} escluso `id`
   * (`selectedChoices`, `selectedChoiceGroups`, `narrativeFieldValues`).
   *
   * Esempio Chierico via multiclasse:
   * ```ts
   * bundleSetups: {
   *   'chierico.divinita': {
   *     selectedChoiceGroups: { ... 3 gruppi Tradizione/Sfera/Fondamento ... },
   *     narrativeFieldValues: { 'nome-divinita': 'Pelor' },
   *   }
   * }
   * ```
   * Le mosse del bundle senza setup interattivo hanno chiave assente.
   * Throws se una mossa del bundle ha `hasMoveSetup === true` ma manca
   * la chiave (sotto-chiave del caller / UI: validare PRIMA di chiamare).
   */
  bundleSetups?: Readonly<Record<string, Omit<MoveRef, 'id'>>>;
  /**
   * Disabilita l'espansione automatica di `move.bundles`. Default: `true`
   * (espansione attiva).
   *
   * **Quando passare `false`**: nel flow `characterCreation` (builder L1)
   * le mosse iniziali del PG sono già tutte esplicite in `input.moves`
   * (es. Mago L1 ha `corpus-arcano` + `mana` + `lanciare-incantesimi-arcani`
   * iterate separatamente dal loop). Espandere il bundle durante il loop
   * rischierebbe di applicare la mossa anchor PRIMA di Divinità con
   * setup completato (ordine non garantito → bundle aggiungerebbe
   * `chierico.divinita` SENZA setup, poi il loop arriverebbe alla mossa
   * con setup e l'idempotenza la skipperebbe). Più semplice e safe:
   * disabilitare il bundle al builder L1.
   *
   * Tutti gli altri caller (level-up `main`/`granted`/`multiclass`)
   * usano il default `true`.
   */
  expandBundle?: boolean;
  /**
   * **FI-84 enhanced (2026-05-24)** — SSOT ricorsiva apply layer.
   * Cascata di mosse concesse via `grantsMoveChoice` su mosse ottenute
   * di seguito (anchor → granted → granted-of-granted → ...). Ogni step
   * applica la mossa scelta come `source: 'granted'` e poi processa il
   * sub-cascata. L'unificazione qui rimpiazza i blocchi custom
   * granted-of-target che vivevano in `applyMulticlassAcquisition`
   * (FI-81 #2 / FI-84 base) e in `applyGrantedAcquisition` (in parte).
   *
   * **Forma**: lista ordinata. `steps[0]` viene applicato come granted
   * della mossa anchor di questo `applyMoveAcquisition`. `steps[1]` come
   * granted-of-`steps[0]`. Etc.
   *
   * **Validazione**: ogni step deve avere `selectedMoveId` valido per la
   * grant della mossa precedente (kind `from-group` / `from-list` →
   * controlla `moveOptions`/group; kind `from-choice-mapping` →
   * controlla `mapping` values; kind `from-choice-move` → non gestito
   * qui, vive come mutazione locale del MoveRef sorgente).
   *
   * **Setup propagation**: `step.selectedSetup` viene proiettato sul
   * MoveRef della mossa granted, prima dell'acquisizione.
   *
   * **Anti-loop**: la cascata è guarda da array slicing
   * (`steps.slice(1)`) — ogni livello consuma 1 step. Finite by design.
   */
  grantedCascadeSteps?: readonly {
    selectedMoveId: string;
    selectedSetup?: Omit<MoveRef, 'id'>;
  }[];
}

/**
 * Applica l'acquisizione di una mossa al Character in modo atomico.
 *
 * Vedi JSDoc del modulo per cosa fa / cosa NON fa.
 *
 * **Cross-class** (FI-30 D6 + FI-17 D7): la mossa acquisita può essere
 * di una classe **diversa** dal PG. Esempio: PG Barbaro che acquisisce
 * `ladro.io-sparo-per-primo` via *Essere il Totem* (FI-30,
 * `source: 'granted'`). Il lookup è fatto via `findMoveByIdGlobal`
 * (cerca in tutte le 12 classi + common). Il `sourceClassId` per
 * `resolveAutoSpellGrant` è quello della mossa (`move.classId`), non
 * del PG: una grant `all-of-level` su `own-class` pesca dalla classe
 * della mossa, non dalla classe del PG.
 *
 * **Idempotenza** (D7-bundle): se `acquiredMove.moveRef.id` è già in
 * `character.moves`, ritorna invariato. Sicurezza per bundle expansion
 * (anchor → bundle members possono toccare mosse già aggiunte in
 * iterazioni precedenti del caller).
 *
 * **Bundle expansion** (D7-bundle): se `move.bundles` è popolato e
 * `acquiredMove.source !== 'bundle'` (anti-loop) e
 * `options.expandBundle !== false`, applica ricorsivamente le mosse
 * del bundle con `source: 'bundle'`. Le mosse del bundle che richiedono
 * setup leggono da `options.bundleSetups[moveId]`.
 *
 * @throws Error se la mossa non è nel catalogo globale, o se una mossa
 *               del bundle richiede setup e `bundleSetups` non lo fornisce.
 */
export function applyMoveAcquisition(
  character: Character,
  acquiredMove: AcquiredMove,
  classDef: ClassDefinition,
  options: ApplyMoveAcquisitionOptions = {},
): Character {
  const move = findMoveByIdGlobal(acquiredMove.moveRef.id);
  if (!move) {
    throw new Error(`Mossa "${acquiredMove.moveRef.id}" non trovata nel catalogo globale.`);
  }
  // Idempotenza (D7-bundle): la mossa è già nel PG → no-op.
  if (hasMoveRefInCharacter(character, acquiredMove.moveRef.id)) {
    return character;
  }
  // 1. Rimuovi mosse soppiantate (replaces)
  const replacedIds = new Set(move.replaces);
  const movesAfterReplace = character.moves.filter((m) => !replacedIds.has(m.id));
  // 2. Aggiungi la mossa acquisita
  const nextMoves = [...movesAfterReplace, acquiredMove.moveRef];
  // 3. Applica spell-grants auto + propaga spell-grants pick
  //
  // **FI-30 (D6)**: per le mosse cross-class, `sourceClassId` deve
  // essere la classe della mossa (definisce la pool spell), non del PG.
  // Per le mosse common (`move.classId === undefined`) fallback alla
  // classe del PG.
  const sourceClassId = (move.classId as ClassId | undefined) ?? classDef.id;
  const requirements = getMoveAcquisitionRequirements(move);
  const autoSpells = requirements.autoSpellGrants.flatMap((g) =>
    resolveAutoSpellGrant(g, sourceClassId),
  );
  const existingSpellIds = new Set(character.spells.map((s) => s.id));
  const newSpells = [...autoSpells, ...acquiredMove.spellsSelected].filter(
    (s) => !existingSpellIds.has(s.id),
  );
  const nextSpells = [...character.spells, ...newSpells];
  const afterAnchor: Character = {
    ...character,
    moves: nextMoves,
    spells: nextSpells,
  };

  // 4. Bundle expansion (D7-bundle): solo se la mossa è anchor di un bundle
  //    e non siamo già dentro l'espansione di un bundle (anti-loop) e il
  //    caller non ha disabilitato esplicitamente.
  const expandBundle = options.expandBundle ?? true;
  const afterBundle =
    move.bundles && acquiredMove.source !== 'bundle' && expandBundle
      ? applyBundleExpansion(afterAnchor, move, options.bundleSetups ?? {}, classDef)
      : afterAnchor;

  // 5. **FI-84 enhanced (2026-05-24)** — Granted cascade ricorsiva.
  //    Se il caller fornisce `grantedCascadeSteps`, applica il primo
  //    step come granted di questa mossa, poi ricorri sui restanti.
  //    Anti-loop garantito dal consumo sequenziale degli step.
  const steps = options.grantedCascadeSteps;
  if (steps !== undefined && steps.length > 0) {
    return applyGrantedCascade(afterBundle, move, steps, classDef, options);
  }
  return afterBundle;
}

/**
 * **FI-84 enhanced (2026-05-24)** — SSOT ricorsiva per la cascata di
 * mosse concesse via `grantsMoveChoice` (anchor → granted →
 * granted-of-granted → ...).
 *
 * Algoritmo: consuma il primo step, verifica che la mossa anchor abbia
 * una grant compatibile (`from-group`, `from-list`,
 * `from-choice-mapping`), valida che `step.selectedMoveId` sia nel pool
 * della grant, e applica la mossa selezionata come `source: 'granted'`
 * via `applyMoveAcquisition` (con `grantedCascadeSteps: steps.slice(1)`
 * per la prossima iterazione). I `bundleSetups` sono propagati.
 *
 * @throws Error se `step.selectedMoveId` non è valido per la grant
 *               dell'anchor, o se l'anchor non ha grant.
 */
function applyGrantedCascade(
  character: Character,
  anchorMove: Move,
  steps: readonly { selectedMoveId: string; selectedSetup?: Omit<MoveRef, 'id'> }[],
  classDef: ClassDefinition,
  parentOptions: ApplyMoveAcquisitionOptions,
): Character {
  const step = steps[0];
  if (step === undefined) return character;
  const grant = anchorMove.grantsMoveChoice;
  if (grant === undefined) {
    throw new Error(
      `Cascata granted: la mossa "${anchorMove.id}" non ha grantsMoveChoice, impossibile applicare lo step "${step.selectedMoveId}".`,
    );
  }
  validateCascadeStepAgainstGrant(grant, step.selectedMoveId, anchorMove.id);
  const grantedMoveRef: MoveRef = step.selectedSetup
    ? { id: step.selectedMoveId, ...step.selectedSetup }
    : { id: step.selectedMoveId, selectedChoices: [] };
  const grantedAcquired: AcquiredMove = {
    moveRef: grantedMoveRef,
    source: 'granted',
    spellsSelected: [],
  };
  // Costruzione opzioni: propaga bundleSetups + restante cascata.
  const nextOptions: ApplyMoveAcquisitionOptions = {};
  if (parentOptions.bundleSetups !== undefined)
    nextOptions.bundleSetups = parentOptions.bundleSetups;
  if (parentOptions.expandBundle !== undefined)
    nextOptions.expandBundle = parentOptions.expandBundle;
  const rest = steps.slice(1);
  if (rest.length > 0) nextOptions.grantedCascadeSteps = rest;
  return applyMoveAcquisition(character, grantedAcquired, classDef, nextOptions);
}

/**
 * **FI-84 enhanced** — Valida che `selectedMoveId` appartenga al pool
 * della grant dell'anchor. Throws con messaggio user-facing se invalido.
 * Per `from-choice-move` (mutazione locale, non nuova acquisizione)
 * throws perché non gestito dalla cascata.
 */
function validateCascadeStepAgainstGrant(
  grant: NonNullable<Move['grantsMoveChoice']>,
  selectedMoveId: string,
  anchorMoveId: string,
): void {
  if (grant.kind === 'from-list') {
    if (!grant.moveOptions.includes(selectedMoveId)) {
      throw new Error(
        `Cascata granted: "${selectedMoveId}" non è nel pool from-list di "${anchorMoveId}".`,
      );
    }
    return;
  }
  if (grant.kind === 'from-choice-mapping') {
    const mappedIds = Object.values(grant.mapping);
    if (!mappedIds.includes(selectedMoveId)) {
      throw new Error(
        `Cascata granted: "${selectedMoveId}" non è nei target del mapping di "${anchorMoveId}".`,
      );
    }
    return;
  }
  if (grant.kind === 'from-group') {
    // Il pool richiede il `classDef.startingChoiceGroups` per essere validato.
    // Skip validazione qui (il caller dovrebbe pre-validare); la mossa
    // viene comunque applicata via `applyMoveAcquisition`.
    return;
  }
  // from-choice-move: mutazione locale di MoveRef, non gestita qui.
  throw new Error(
    `Cascata granted: kind "${grant.kind}" della grant di "${anchorMoveId}" non è gestito dalla cascata SSOT (vive come mutazione locale).`,
  );
}

/**
 * **D7-bundle** — espande il bundle di una mossa anchor applicando ogni
 * mossa del bundle con `source: 'bundle'`. Vive qui (non in `bundle.ts`)
 * per evitare import circolari: la ricorsione 1-livello è bloccata da
 * `acquiredMove.source === 'bundle'` in {@link applyMoveAcquisition}.
 *
 * @throws Error se una mossa del bundle richiede setup interattivo
 *               (`hasMoveSetup === true`) ma `bundleSetups[moveId]` è
 *               assente.
 */
function applyBundleExpansion(
  character: Character,
  anchorMove: Move,
  bundleSetups: Readonly<Record<string, Omit<MoveRef, 'id'>>>,
  classDef: ClassDefinition,
): Character {
  const movesToApply = getBundleMovesToApply(anchorMove, character);
  let next = character;
  for (const bundleMove of movesToApply) {
    const setup = bundleSetups[bundleMove.id];
    if (hasMoveSetup(bundleMove) && setup === undefined) {
      throw new Error(
        `Bundle di "${anchorMove.id}": la mossa "${bundleMove.id}" richiede setup interattivo, ma "bundleSetups['${bundleMove.id}']" non è stato fornito.`,
      );
    }
    const bundleMoveRef: MoveRef = setup
      ? { id: bundleMove.id, ...setup }
      : { id: bundleMove.id, selectedChoices: [] };
    const acquired: AcquiredMove = {
      moveRef: bundleMoveRef,
      source: 'bundle',
      spellsSelected: [],
    };
    next = applyMoveAcquisition(next, acquired, classDef);
  }
  return next;
}
